import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { AbstractControl, ControlContainer, FormArray, FormGroup } from "@angular/forms";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { DataRowState } from "@tylertech/shared-module";
import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { MailingAddressViewModel } from "@tylertech/tyler-api-types";

import { ContactService } from "../../services";

@Component({
    selector: "mailing-address",
    templateUrl: "./mailingAddress.tmpl.html",
    standalone: false
})
export class MailingAddressComponent implements OnInit, OnDestroy
{
    private readonly $contactService = inject(ContactService);
    private readonly controlContainer = inject(ControlContainer, { optional: true });

    private readonly destructor$ = new Subject<void>();
    readonly formGroup: FormGroup;


    @Input() readonly usages: string;
    @Input() private readonly mailingAddress: MailingAddressViewModel;
    @Input() readonly viewOnly: boolean;
    @Input() private readonly formArrayName?: string;

    @Input() private readonly refresh: Observable<void>;

    @Output() private readonly edit = new EventEmitter<MailingAddressViewModel>();
    @Output() private readonly primaryChanged = new EventEmitter<MailingAddressViewModel>();
    @Output() private readonly forwardingChanged = new EventEmitter<MailingAddressViewModel>();
    @Output() private readonly certify = new EventEmitter<MailingAddressViewModel>();
    @Output() private readonly deleted = new EventEmitter<MailingAddressViewModel>();

    showUsages: boolean;
    showCertify: boolean;
    isEditAddress: boolean;

    constructor()
    {
        this.formGroup = new FormGroup({
            attention: LgdFormControlFactory.text(undefined, false, {}),
            line1: LgdFormControlFactory.text(undefined, true, {
                maxLength: 50,
            }),
            line2: LgdFormControlFactory.text(undefined, false, {
                maxLength: 50,
            }),
            city: LgdFormControlFactory.text(undefined, true, {
                maxLength: 50
            }),
            state: LgdFormControlFactory.text(undefined, true, {
                maxLength: 50,
            }),
            postalCode: LgdFormControlFactory.text(undefined, true, {
                maxLength: 14,
            }),
            country: LgdFormControlFactory.text(undefined, false, {
                maxLength: 25,
            }),
            isPrimary: LgdFormControlFactory.checkbox(),
            isForwarding: LgdFormControlFactory.checkbox(),
            isCertified: LgdFormControlFactory.checkbox(),
        });
    }

    private get parentForm(): FormGroup | undefined
    {
        return this.controlContainer?.control as FormGroup || undefined;
    }

    private get formArray(): FormArray
    {
        return this.formArrayName
            ? this.parentForm?.get(this.formArrayName) as FormArray
            : undefined;
    }

    private get attentionControl(): AbstractControl<string> { return this.formGroup.get("attention"); }
    private get line1Control(): AbstractControl<string> { return this.formGroup.get("line1"); }
    private get line2Control(): AbstractControl<string> { return this.formGroup.get("line2"); }
    private get cityControl(): AbstractControl<string> { return this.formGroup.get("city"); }
    private get stateControl(): AbstractControl<string> { return this.formGroup.get("state"); }
    private get postalCodeControl(): AbstractControl<string> { return this.formGroup.get("postalCode"); }
    private get countryControl(): AbstractControl<string> { return this.formGroup.get("country"); }
    private get isPrimaryControl(): AbstractControl<boolean> { return this.formGroup.get("isPrimary"); }
    private get isForwardingControl(): AbstractControl<boolean> { return this.formGroup.get("isForwarding"); }
    private get isCertifiedControl(): AbstractControl<boolean> { return this.formGroup.get("isCertified"); }

    get fullMailingAddress(): string { return this.mailingAddress.FullMailingAddress; }
    get certificationResults(): string { return this.mailingAddress.CertificationResults; }

    get showCertification(): boolean
    {
        return !!this.mailingAddress.CertificationResults;
    }

    ngOnDestroy(): void
    {
        if (this.formArray)
        {
            const index = this.formArray.controls.indexOf(this.formGroup);
            if (index > -1)
            {
                this.formArray?.removeAt(index);
            }
        }

        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.isEditAddress = this.mailingAddress.RowState === DataRowState.Added;

        if (this.formArray)
        {
            this.formArray?.push(this.formGroup);
        }

        Promise.all(
            [
                this.$contactService.getShowCertify()
                    .then((result: boolean) =>
                    {
                        this.showCertify = result;
                    })
            ])
            .finally(() =>
            {
                this.updateControlValues();
            });

        this.refresh.pipe(
            takeUntil(this.destructor$))
            .subscribe(() =>
            {
                /*
                 * We don't want to retrigger any of our value change subscriptions since this is a reaction to actions
                 * from the parent, which may be sourced back to user input here.
                 */
                this.updateControlValues();
            });

        subscribeToValueChanges(this.attentionControl, (newValue: string) =>
        {
            this.mailingAddress.Attention = newValue;

            this.setFullMA(this.mailingAddress);
        });

        subscribeToValueChanges(this.line1Control, (newValue: string) =>
        {
            this.mailingAddress.Line1 = newValue;
            this.setFullMA(this.mailingAddress);
        });

        subscribeToValueChanges(this.line2Control, (newValue: string) =>
        {
            this.mailingAddress.Line2 = newValue;
            this.setFullMA(this.mailingAddress);
        });

        subscribeToValueChanges(this.cityControl, (newValue: string) =>
        {
            this.mailingAddress.City = newValue;

            this.setFullMA(this.mailingAddress);
        });

        subscribeToValueChanges(this.stateControl, (newValue: string) =>
        {
            this.mailingAddress.State = newValue;

            this.setFullMA(this.mailingAddress);
        });

        subscribeToValueChanges(this.postalCodeControl, (newValue: string) =>
        {
            this.mailingAddress.PostalCode = newValue;
            this.setFullMA(this.mailingAddress);
        });

        subscribeToValueChanges(this.countryControl, (newValue: string) =>
        {
            this.mailingAddress.Country = newValue;

            this.setFullMA(this.mailingAddress);
        });

        setEnabled(this.isPrimaryControl, !this.viewOnly);
        subscribeToValueChanges(this.isPrimaryControl, (newValue: boolean) =>
        {
            // TODO - this may be changed by the event subscription.
            this.mailingAddress.IsPrimary = newValue;

            this.primaryChanged.emit(this.mailingAddress);
        });

        setEnabled(this.isForwardingControl, !this.viewOnly);
        subscribeToValueChanges(this.isForwardingControl, (newValue: boolean) =>
        {
            // TODO - this may be changed by the event subscription.
            this.mailingAddress.IsForwarding = newValue;

            this.forwardingChanged.emit(this.mailingAddress);
        });

        setEnabled(this.isCertifiedControl, false);
        // subscribeToValueChanges(this.isCertifiedControl, (_newValue: boolean) =>
        // {
        //     throw new Error("NotImplemented");
        // });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private updateControlValues(): void
    {
        this.attentionControl.setValue(this.mailingAddress.Attention, { emitEvent: false });
        this.line1Control.setValue(this.mailingAddress.Line1, { emitEvent: false });
        this.line2Control.setValue(this.mailingAddress.Line2, { emitEvent: false });

        this.cityControl.setValue(this.mailingAddress.City, { emitEvent: false });
        this.stateControl.setValue(this.mailingAddress.State, { emitEvent: false });
        this.postalCodeControl.setValue(this.mailingAddress.PostalCode, { emitEvent: false });

        this.countryControl.setValue(this.mailingAddress.Country, { emitEvent: false });

        this.isPrimaryControl.setValue(this.mailingAddress.IsPrimary, { emitEvent: false });
        this.isForwardingControl.setValue(this.mailingAddress.IsForwarding, { emitEvent: false });
        this.isCertifiedControl.setValue(this.mailingAddress.IsCertified, { emitEvent: false });
    }

    editClicked(): void
    {
        this.isEditAddress = !this.isEditAddress;
        this.edit.emit(this.mailingAddress);
    }

    deleteClicked(): void
    {
        if (this.usages)
        {
            this.showUsages = true;
        }
        else
        {
            this.deleted.emit(this.mailingAddress);
            this.mailingAddress.RowState = DataRowState.Deleted;
        }
    }

    getCertifyTooltip(): string
    {
        return this.mailingAddress.IsCertified ? "Uncertify" : "Certify";
    }

    certifyClicked(): void
    {
        this.certify.emit(this.mailingAddress);
    }

    private itemChanged(): void
    {
        if (!this.mailingAddress)
        {
            return;
        }

        if (this.mailingAddress.RowState === DataRowState.Unchanged)
        {
            this.mailingAddress.RowState = DataRowState.Modified;
        }
    }

    private setFullMA(item: MailingAddressViewModel): void
    {
        let fullAddress = "";

        if (item.Attention)
        {
            fullAddress += item.Attention + "\n";
        }

        if (item.Line1)
        {
            fullAddress += item.Line1 + "\n";
        }

        if (item.Line2)
        {
            fullAddress += item.Line2 + "\n";
        }

        if (item.City)
        {
            fullAddress += item.City + ", ";
        }

        if (item.State)
        {
            fullAddress += item.State + " ";
        }

        if (item.PostalCode)
        {
            fullAddress += item.PostalCode;
        }

        if (item.Country)
        {
            fullAddress += "\n" + item.Country;
        }

        const joinedAddress = [
            this.mailingAddress.Attention,
            this.mailingAddress.Line1,
            this.mailingAddress.Line2,
            `${this.mailingAddress.City}, ${this.mailingAddress.State} ${this.mailingAddress.PostalCode}`,
            this.mailingAddress.Country,
        ].filter(x => x)
            .join("\n");

         
        console.warn(`${MailingAddressComponent.name} | ${this.setFullMA.name}`, { joinedAddress, fullAddress });

        item.FullMailingAddress = fullAddress;

        this.itemChanged();
    }
}
